import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import UploadButton from "@/components/UploadButton";
import { toast } from "sonner";
import {
  Bookmark, Database, FileText, Flame, Sparkles,
  HelpCircle, CheckCircle2, Medal, Brain,
  Image as ImageIcon, Paperclip, Send, MoreHorizontal,
  MessageSquare, Trash2, Edit3, Users as UsersIcon, Lock, PenSquare, UserCheck, Pin,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// ────────── 7 Academic Reactions ──────────
export const REACTIONS = [
  { kind: "bookmarked",        idx: 0, label: "Bookmark",          icon: Bookmark,     tint: "slate",   hint: "Save for future reference" },
  { kind: "provocative",       idx: 1, label: "Provocative",        icon: Flame,        tint: "red",     hint: "Challenges the status quo" },
  { kind: "novel",             idx: 2, label: "Novel",              icon: Sparkles,     tint: "teal",    hint: "First-of-its-kind" },
  { kind: "skeptical",         idx: 3, label: "Skeptical",          icon: HelpCircle,   tint: "amber",   hint: "Flawed methodology / insufficient data" },
  { kind: "validated",         idx: 4, label: "Validated",          icon: CheckCircle2, tint: "green",   hint: "Intellectually sound" },
  { kind: "merit_badge",       idx: 5, label: "Merit badge",        icon: Medal,        tint: "gold",    hint: "Rare gold — exceptional value" },
  { kind: "thought_provoking", idx: 6, label: "Thought-provoking",  icon: Brain,        tint: "purple",  hint: "Deep reflection required" },
];

const TINT_BG = {
  slate: "text-slate-500 hover:bg-slate-100",
  emerald: "text-emerald-600 hover:bg-emerald-50",
  blue: "text-blue-600 hover:bg-blue-50",
  red: "text-red-600 hover:bg-red-50",
  teal: "text-teal hover:bg-teal-50",
  amber: "text-amber-600 hover:bg-amber-50",
  green: "text-green-600 hover:bg-green-50",
  gold: "text-yellow-600 hover:bg-yellow-50",
  purple: "text-purple-600 hover:bg-purple-50",
};
const TINT_ACTIVE = {
  slate: "bg-slate-200 text-slate-900 ring-1 ring-slate-400",
  emerald: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-400",
  blue: "bg-blue-100 text-blue-800 ring-1 ring-blue-400",
  red: "bg-red-100 text-red-800 ring-1 ring-red-400",
  teal: "bg-teal-100 text-teal-800 ring-1 ring-teal",
  amber: "bg-amber-100 text-amber-800 ring-1 ring-amber-400",
  green: "bg-green-100 text-green-800 ring-1 ring-green-400",
  gold: "bg-gradient-to-br from-yellow-300 to-amber-400 text-amber-950 ring-2 ring-amber-500 shadow-sm",
  purple: "bg-gradient-to-br from-purple-200 to-indigo-300 text-purple-900 ring-2 ring-purple-500 shadow-sm",
};

// ────────── Converse page ──────────
export default function Converse() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const focusPostId = searchParams.get("post");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  // When arriving from a notification about your own post, land on "My posts"
  // so the highlighted item is the very first thing the user sees.
  const [view, setView] = useState(() => (focusPostId ? "mine" : "all"));
  const [bookmarksCount, setBookmarksCount] = useState(0);
  const [myPostsCount, setMyPostsCount] = useState(0);
  const [flashId, setFlashId] = useState(null);
  const didScrollRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let path = "/posts";
      if (view === "bookmarks") path = "/me/bookmarks";
      else if (view === "mine" && user) path = `/posts?author_id=${user.id}`;
      else if (view === "following" && user) path = "/posts?following=true";
      const { data } = await api.get(path);
      setPosts(data);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }, [view, user]);

  const refreshCounts = useCallback(async () => {
    if (!user) return;
    try {
      const [bm, mp] = await Promise.all([
        api.get("/me/bookmarks"),
        api.get(`/posts?author_id=${user.id}`),
      ]);
      setBookmarksCount(bm.data.length);
      setMyPostsCount(mp.data.length);
    } catch (_) {}
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { refreshCounts(); }, [refreshCounts, posts.length]);

  // After posts render, scroll the deep-linked post into view + flash it.
  // If the post isn't in the current view (e.g. it was deleted, or the user
  // is on a filtered view that hides it), fall back to "all".
  useEffect(() => {
    if (!focusPostId || loading || didScrollRef.current) return;
    const target = posts.find((p) => p.id === focusPostId);
    if (target) {
      didScrollRef.current = true;
      const el = document.querySelector(`[data-post-id="${focusPostId}"]`);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          setFlashId(focusPostId);
          setTimeout(() => setFlashId(null), 2500);
        }, 150);
      }
      // Strip the ?post param so refreshes don't re-flash
      const sp = new URLSearchParams(searchParams);
      sp.delete("post");
      setSearchParams(sp, { replace: true });
    } else if (view === "mine" && posts.length) {
      // Post not in "My posts" — probably someone else's → switch to All feed
      setView("all");
    }
  }, [focusPostId, loading, posts, view, searchParams, setSearchParams]);

  const afterChange = async () => { await load(); refreshCounts(); };

  return (
    <div className="et-section py-8" data-testid="converse-page">
      <div
        className="mb-6 rounded-md border-2 border-red-600 bg-red-50 px-4 py-3 text-center text-sm sm:text-base font-bold text-red-700"
        data-testid="converse-retention-notice"
      >
        NOTICE: All posts on this page are automatically deleted after 30 days.
      </div>
      <div className="grid lg:grid-cols-3 gap-8">
      {/* Left column — member card */}
      <aside className="lg:col-span-1 space-y-5">
        {user
          ? <MeCard bookmarksCount={bookmarksCount} myPostsCount={myPostsCount} onOpenBookmarks={() => setView("bookmarks")} onOpenMine={() => setView("mine")}/>
          : <SignInPrompt />}
        <ReactionLegend />
      </aside>

      {/* Right column — composer + feed */}
      <main className="lg:col-span-2 space-y-5">
        {user && view === "all" && <Composer onPosted={afterChange} />}

        {user && (
          <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto" data-testid="converse-view-tabs">
            <ViewTab active={view === "all"} onClick={() => setView("all")} testid="converse-tab-all">
              <MessageSquare className="h-3.5 w-3.5"/>All posts
            </ViewTab>
            <ViewTab active={view === "following"} onClick={() => setView("following")} testid="converse-tab-following">
              <UserCheck className="h-3.5 w-3.5"/>Following
            </ViewTab>
            <ViewTab active={view === "mine"} onClick={() => setView("mine")} testid="converse-tab-mine">
              <PenSquare className="h-3.5 w-3.5"/>My posts
              {myPostsCount > 0 && (
                <span className={`ml-1.5 text-[10px] font-semibold rounded px-1.5 py-0.5 ${view === "mine" ? "bg-teal text-white" : "bg-slate-100 text-slate-600"}`}>
                  {myPostsCount}
                </span>
              )}
            </ViewTab>
            <ViewTab active={view === "bookmarks"} onClick={() => setView("bookmarks")} testid="converse-tab-bookmarks">
              <Bookmark className="h-3.5 w-3.5"/>My bookmarks
              {bookmarksCount > 0 && (
                <span className={`ml-1.5 text-[10px] font-semibold rounded px-1.5 py-0.5 ${view === "bookmarks" ? "bg-teal text-white" : "bg-slate-100 text-slate-600"}`}>
                  {bookmarksCount}
                </span>
              )}
            </ViewTab>
          </div>
        )}

        {loading ? (
          <div className="et-card p-12 text-center text-slate-500">Loading feed…</div>
        ) : posts.length === 0 ? (
          <div className="et-card p-16 text-center text-slate-500" data-testid={`converse-${view}-empty`}>
            {view === "bookmarks" ? (
              <>
                <Bookmark className="h-8 w-8 mx-auto mb-3 text-slate-300"/>
                No bookmarks yet. Tap the <Bookmark className="inline h-3.5 w-3.5 -mt-0.5"/> on any post to save it for later.
              </>
            ) : view === "mine" ? (
              <>
                <PenSquare className="h-8 w-8 mx-auto mb-3 text-slate-300"/>
                You haven't posted yet. Head back to <button onClick={() => setView("all")} className="text-teal hover:underline font-medium">All posts</button> to share your first thought.
              </>
            ) : view === "following" ? (
              <>
                <UserCheck className="h-8 w-8 mx-auto mb-3 text-slate-300"/>
                You aren't following anyone yet — or they haven't posted. Browse <Link to="/campus" className="text-teal hover:underline font-medium">Campus</Link> to find scholars to follow.
              </>
            ) : (
              <>
                <MessageSquare className="h-8 w-8 mx-auto mb-3 text-slate-300"/>
                The commons is quiet. Be the first to start a conversation.
              </>
            )}
          </div>
        ) : (
          posts.map((p) => <PostCard key={p.id} post={p} onChanged={afterChange} flashed={flashId === p.id}/>)
        )}
      </main>
      </div>
    </div>
  );
}

function ViewTab({ active, onClick, testid, children }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 -mb-px border-b-2 transition ${
        active
          ? "border-teal text-teal"
          : "border-transparent text-slate-500 hover:text-navy"
      }`}
    >
      {children}
    </button>
  );
}

function SignInPrompt() {
  return (
    <div className="et-card p-6" data-testid="converse-signin-prompt">
      <h3 className="font-serif text-xl text-navy">Join the conversation</h3>
      <p className="text-sm text-slate-600 mt-2">
        Converse is the commons of Talkademia — post, follow, react with seven academic signals.
      </p>
      <Link to="/login" className="inline-block mt-4 text-teal font-semibold hover:underline">Sign in →</Link>
    </div>
  );
}

function MeCard({ bookmarksCount = 0, myPostsCount = 0, onOpenBookmarks, onOpenMine }) {
  const { user } = useAuth();
  return (
    <div className="et-card p-6" data-testid="converse-me-card">
      <div className="flex items-start gap-4">
        <div className="h-16 w-16 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
          {user.avatar_url ? <img src={user.avatar_url} alt="" className="h-full w-full object-cover"/> : <UsersIcon className="h-5 w-5 text-slate-400"/>}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-serif text-lg text-navy truncate">{user.display_name}</div>
          <div className="text-xs text-slate-500">@{user.username}</div>
          <div className="mt-1.5 flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] capitalize">{user.role.replace("_", " ")}</Badge>
            {user.tier === "premium" && <Badge className="bg-teal text-white text-[10px]">Premium</Badge>}
          </div>
        </div>
      </div>
      {user.bio && <p className="text-xs text-slate-600 leading-relaxed mt-4 line-clamp-4">{user.bio}</p>}
      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between text-xs">
        <Link to={`/u/${user.username}`} className="text-teal hover:underline font-medium" data-testid="me-card-view-profile">View public profile →</Link>
        <Link to="/profile" className="text-slate-500 hover:text-navy" data-testid="me-card-edit">Edit</Link>
      </div>
      <button
        onClick={onOpenMine}
        className="mt-3 pt-3 border-t border-slate-100 w-full flex items-center justify-between text-xs hover:text-teal group"
        data-testid="me-card-open-mine"
      >
        <span className="text-slate-500 group-hover:text-teal inline-flex items-center gap-1">
          <PenSquare className="h-3.5 w-3.5"/>My posts
        </span>
        <span className="font-semibold text-navy group-hover:text-teal inline-flex items-center gap-1">
          {myPostsCount}
          <span className="text-slate-300 group-hover:text-teal">→</span>
        </span>
      </button>
      <button
        onClick={onOpenBookmarks}
        className="mt-2 w-full flex items-center justify-between text-xs hover:text-teal group"
        data-testid="me-card-open-bookmarks"
      >
        <span className="text-slate-500 group-hover:text-teal inline-flex items-center gap-1">
          <Bookmark className="h-3.5 w-3.5"/>My bookmarks
        </span>
        <span className="font-semibold text-navy group-hover:text-teal inline-flex items-center gap-1">
          {bookmarksCount}
          <span className="text-slate-300 group-hover:text-teal">→</span>
        </span>
      </button>
    </div>
  );
}

function ReactionLegend() {
  return (
    <div className="et-card p-5" data-testid="converse-legend">
      <h4 className="font-serif text-sm text-navy mb-3">The seven academic signals</h4>
      <ul className="space-y-2 text-xs">
        {REACTIONS.map(({ kind, label, icon: I, tint, hint }) => (
          <li key={kind} className="flex items-start gap-2.5" data-testid={`legend-${kind}`}>
            <span className={`mt-0.5 h-6 w-6 rounded flex items-center justify-center ${TINT_ACTIVE[tint]} flex-shrink-0`}>
              <I className="h-3.5 w-3.5"/>
            </span>
            <div>
              <div className="font-semibold text-navy">{label}</div>
              <div className="text-slate-500 leading-tight">{hint}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Composer({ onPosted }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [attachment, setAttachment] = useState({ url: "", kind: "", name: "" });
  const [showImg, setShowImg] = useState(false);
  const [showFile, setShowFile] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() && !attachment.url) { toast.error("Write something or attach a file"); return; }
    setBusy(true);
    try {
      await api.post("/posts", {
        body: body.trim(),
        attachment_url: attachment.url || "",
        attachment_kind: attachment.kind || "",
        attachment_name: attachment.name || "",
      });
      setBody("");
      setAttachment({ url: "", kind: "", name: "" });
      setShowImg(false); setShowFile(false);
      onPosted();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="et-card p-5 space-y-3" data-testid="converse-composer">
      <Textarea
        rows={3}
        value={body}
        onChange={(e) => setBody(e.target.value)}
        placeholder="Share a thought, a paper you read, a question…"
        maxLength={10000}
        className="border-slate-200 focus-visible:border-teal resize-none"
        data-testid="composer-body"
      />
      {attachment.url && attachment.kind === "image" && (
        <div className="relative rounded-md overflow-hidden border border-slate-200">
          <img src={attachment.url} alt="" className="max-h-64 w-full object-contain bg-slate-50"/>
          <button type="button" onClick={() => setAttachment({ url:"",kind:"",name:"" })} className="absolute top-2 right-2 h-6 w-6 rounded-full bg-black/60 text-white text-xs">×</button>
        </div>
      )}
      {attachment.url && attachment.kind === "file" && (
        <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm">
          <Paperclip className="h-4 w-4 text-teal"/>
          <span className="flex-1 truncate text-navy">{attachment.name || "Attachment"}</span>
          <button type="button" onClick={() => setAttachment({ url:"",kind:"",name:"" })} className="text-slate-400 hover:text-red-600">Remove</button>
        </div>
      )}
      {(showImg || showFile) && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
          <UploadButton
            kind={showImg ? "image" : "file"}
            value=""
            onChange={(url, meta) => setAttachment({ url, kind: showImg ? "image" : "file", name: (meta && meta.name) || "" })}
            label={showImg ? "Choose image" : "Choose file (PDF, Word, Excel, PPT, TXT, ZIP)"}
          />
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => { setShowImg((v) => !v); setShowFile(false); }}
            className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-teal"
            aria-label="Attach image"
            data-testid="composer-attach-image"
          ><ImageIcon className="h-4 w-4"/></button>
          <button
            type="button"
            onClick={() => { setShowFile((v) => !v); setShowImg(false); }}
            className="p-2 rounded hover:bg-slate-100 text-slate-500 hover:text-teal"
            aria-label="Attach file"
            data-testid="composer-attach-file"
          ><Paperclip className="h-4 w-4"/></button>
          <span className="text-[11px] text-slate-400 ml-2">{body.length}/10000</span>
        </div>
        <Button
          type="submit"
          disabled={busy || (!body.trim() && !attachment.url)}
          className="bg-teal hover:bg-teal-700 text-white"
          data-testid="composer-submit"
        >{busy ? "Posting…" : <><Send className="h-4 w-4 mr-1.5"/>Post</>}</Button>
      </div>
    </form>
  );
}

function PostCard({ post, onChanged, flashed }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(post.body);
  const [showComments, setShowComments] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isMine = user && user.id === post.author_id;
  const isAdmin = user?.username === "jah1433";

  const togglePin = async () => {
    try {
      if (post.is_pinned) {
        await api.delete(`/posts/${post.id}/pin`);
        toast.success("Unpinned");
      } else {
        await api.post(`/posts/${post.id}/pin`);
        toast.success("Pinned to top of Converse");
      }
      onChanged();
    } catch (e) { toast.error(formatError(e)); }
  };

  const react = async (kind) => {
    if (!user) { navigate("/login"); return; }
    try {
      await api.post(`/posts/${post.id}/react/${kind}`);
      onChanged();
    } catch (e) { toast.error(formatError(e)); }
  };

  const saveEdit = async () => {
    if (!draft.trim()) return toast.error("Body required");
    try {
      await api.patch(`/posts/${post.id}`, { body: draft.trim() });
      setEditing(false);
      toast.success("Updated");
      onChanged();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await api.delete(`/posts/${post.id}`);
      toast.success("Deleted");
      onChanged();
    } catch (e) { toast.error(formatError(e)); }
  };

  const when = new Date(post.created_at);
  return (
    <article
      className={`et-card p-5 transition-all ${flashed ? "ring-2 ring-teal ring-offset-2 bg-teal-50/40" : ""} ${post.is_pinned ? "border-amber-300 bg-amber-50/40" : ""}`}
      data-testid={`post-${post.id}`}
      data-post-id={post.id}
    >
      {post.is_pinned && (
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] text-amber-700 font-semibold mb-2" data-testid={`post-pinned-${post.id}`}>
          <Pin className="h-3.5 w-3.5"/>Pinned by admin
        </div>
      )}
      <header className="flex items-start gap-3">
        <Link to={`/u/${post.author_username}`} className="h-11 w-11 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center hover:border-teal">
          {post.author_avatar_url
            ? <img src={post.author_avatar_url} alt="" className="h-full w-full object-cover"/>
            : <UsersIcon className="h-4 w-4 text-slate-400"/>}
        </Link>
        <div className="flex-1 min-w-0">
          <Link to={`/u/${post.author_username}`} className="font-semibold text-navy hover:text-teal">{post.author_name}</Link>
          {post.author_tier === "premium" && <Badge className="bg-teal text-white text-[9px] ml-1.5">Premium</Badge>}
          <div className="text-xs text-slate-500 mt-0.5">
            {when.toLocaleString("en", { dateStyle: "medium", timeStyle: "short" })}
            {post.edited_at && <span className="italic ml-1">· edited</span>}
          </div>
        </div>
        {isMine && (
          <div className="relative">
            <button onClick={() => setMenuOpen((v) => !v)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" aria-label="Post menu" data-testid={`post-menu-${post.id}`}>
              <MoreHorizontal className="h-4 w-4"/>
            </button>
            {menuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)}/>
                <div className="absolute right-0 mt-1 w-44 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1 text-sm">
                  <button onClick={() => { setMenuOpen(false); setEditing(true); }} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2" data-testid={`post-edit-${post.id}`}>
                    <Edit3 className="h-3.5 w-3.5"/>Edit
                  </button>
                  {isAdmin && (
                    <button onClick={() => { setMenuOpen(false); togglePin(); }} className="w-full text-left px-3 py-1.5 hover:bg-amber-50 text-amber-700 flex items-center gap-2" data-testid={`post-pin-${post.id}`}>
                      <Pin className="h-3.5 w-3.5"/>{post.is_pinned ? "Unpin from top" : "Pin to top"}
                    </button>
                  )}
                  <button onClick={() => { setMenuOpen(false); remove(); }} className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600 flex items-center gap-2" data-testid={`post-delete-${post.id}`}>
                    <Trash2 className="h-3.5 w-3.5"/>Delete
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </header>

      {editing ? (
        <div className="mt-3 space-y-2">
          <Textarea rows={4} value={draft} onChange={(e) => setDraft(e.target.value)} data-testid={`post-edit-input-${post.id}`}/>
          <div className="flex gap-2">
            <Button size="sm" onClick={saveEdit} className="bg-teal hover:bg-teal-700 text-white">Save</Button>
            <Button size="sm" variant="outline" onClick={() => { setEditing(false); setDraft(post.body); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          {post.body && <p className="mt-3 text-slate-800 whitespace-pre-wrap leading-relaxed">{post.body}</p>}
          {post.attachment_url && post.attachment_kind === "image" && (
            <img src={post.attachment_url} alt="" className="mt-3 rounded-md border border-slate-200 max-h-[500px] w-full object-contain bg-slate-50"/>
          )}
          {post.attachment_url && post.attachment_kind === "file" && (
            <a href={post.attachment_url} target="_blank" rel="noopener noreferrer" className="mt-3 flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm hover:border-teal hover:text-teal">
              <Paperclip className="h-4 w-4"/>{post.attachment_name || "Attachment"}
            </a>
          )}
        </>
      )}

      {/* Reaction bar */}
      <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-1">
        {!user && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal mr-1.5" data-testid={`react-lock-${post.id}`}>
            <Lock className="h-3 w-3"/>
            <span className="hidden sm:inline">React to unlock —</span>
            <Link to="/login" className="underline hover:text-teal-700">sign in</Link>
          </span>
        )}
        <TooltipProvider delayDuration={120}>
          {REACTIONS.map(({ kind, label, icon: I, tint }) => {
            const count = post.reactions?.[kind] || 0;
            const on = post.my_reactions?.includes(kind);
            const btn = (
              <button
                key={kind}
                onClick={() => react(kind)}
                title={user ? label : undefined}
                data-testid={`react-${kind}-${post.id}`}
                className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded transition ${on ? TINT_ACTIVE[tint] : TINT_BG[tint]} ${!user ? "opacity-80 hover:opacity-100" : ""}`}
              >
                <I className="h-3.5 w-3.5"/>
                {count > 0 && <span>{count}</span>}
              </button>
            );
            if (user) return btn;
            return (
              <Tooltip key={kind}>
                <TooltipTrigger asChild>{btn}</TooltipTrigger>
                <TooltipContent side="top" className="bg-navy text-white text-xs border-navy">
                  <span className="font-semibold">{label}</span> · Sign in to provide peer feedback
                </TooltipContent>
              </Tooltip>
            );
          })}
        </TooltipProvider>
        <button
          onClick={() => setShowComments((v) => !v)}
          className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded text-slate-500 hover:bg-slate-100 hover:text-navy"
          data-testid={`comments-toggle-${post.id}`}
        >
          <MessageSquare className="h-3.5 w-3.5"/>
          <span>Reply{post.comments_count > 0 && ` · ${post.comments_count}`}</span>
        </button>
      </div>

      {showComments && <PostCommentsThread postId={post.id} onChanged={onChanged}/>}
    </article>
  );
}

function PostCommentsThread({ postId, onChanged }) {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/posts/${postId}/comments`);
    setRows(data);
  }, [postId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;
    setBusy(true);
    try {
      await api.post(`/posts/${postId}/comments`, { body: draft.trim() });
      setDraft("");
      await load();
      onChanged();
    } catch (e) { toast.error(formatError(e)); }
    finally { setBusy(false); }
  };

  const removeC = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/comments/${cid}`);
      await load();
      onChanged();
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="mt-4 pt-3 border-t border-slate-100 space-y-3" data-testid={`comments-thread-${postId}`}>
      {rows.map((c) => {
        const mine = user && user.id === c.user_id;
        return (
          <div key={c.id} className="flex items-start gap-2.5 group">
            <div className="h-8 w-8 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {c.user_avatar_url ? <img src={c.user_avatar_url} alt="" className="h-full w-full object-cover"/> : <UsersIcon className="h-3 w-3 text-slate-400"/>}
            </div>
            <div className="flex-1 min-w-0 rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
              <div className="flex items-center gap-2 text-xs">
                <Link to={`/u/${c.user_username}`} className="font-semibold text-navy hover:text-teal">{c.user_name}</Link>
                <span className="text-slate-400">{new Date(c.created_at).toLocaleDateString()}</span>
                {mine && (
                  <button onClick={() => removeC(c.id)} className="ml-auto text-slate-400 hover:text-red-600 opacity-0 group-hover:opacity-100" data-testid={`pc-delete-${c.id}`}>
                    <Trash2 className="h-3.5 w-3.5"/>
                  </button>
                )}
              </div>
              <p className="text-sm text-slate-700 mt-0.5 whitespace-pre-wrap">{c.body}</p>
            </div>
          </div>
        );
      })}
      {user && (
        <form onSubmit={submit} className="flex items-start gap-2.5">
          <Textarea
            rows={2}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Reply…"
            className="flex-1 text-sm resize-none"
            data-testid={`pc-input-${postId}`}
          />
          <Button type="submit" size="sm" disabled={busy || !draft.trim()} className="bg-teal hover:bg-teal-700 text-white">
            {busy ? "…" : <Send className="h-4 w-4"/>}
          </Button>
        </form>
      )}
    </div>
  );
}
